import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createCycle,
  updateCycle,
  deleteCycle,
  activateCycle,
  closeCycle,
  reopenCycle,
  cloneCycle,
  triggerRun,
  stopRun,
  archiveInbox,
  dismissInviteBanner,
  transitionCycleDefect,
  rebindCycleDefect,
  captureReportSnapshot,
} from "./cycles.api";

import { useAuth } from "@/common/hooks/useAuth";
import type {
  CreateCyclePayload,
  UpdateCyclePayload,
  CloseCyclePayload,
  ReopenCyclePayload,
  CloneCyclePayload,
  TriggerRunPayload,
  CycleDetail,
} from "@/common/types/Cycle";

export const useCreateCycle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCyclePayload) => createCycle(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["releaseFolders"] });
    },
  });
};

export const useUpdateCycle = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCyclePayload) => updateCycle(cycleId, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ["cycle", cycleId] });
      const prev = queryClient.getQueryData<CycleDetail>(["cycle", cycleId]);
      queryClient.setQueryData<CycleDetail>(["cycle", cycleId], (old) =>
        old ? { ...old, ...payload } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["cycle", cycleId], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
    },
  });
};

export const useDeleteCycle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleId: number) => deleteCycle(cycleId),
    onSuccess: (_data, cycleId) => {
      const deletedCycleId = Number(cycleId);

      queryClient.removeQueries({ queryKey: ["cycle", cycleId] });

      queryClient.setQueriesData({ queryKey: ["cycles"] }, (old: any) => {
        if (!old) return old;

        if (Array.isArray(old)) {
          return old.filter((item) => Number(item?.id) !== deletedCycleId);
        }

        if (Array.isArray(old?.content)) {
          return {
            ...old,
            content: old.content.filter((item: any) => Number(item?.id) !== deletedCycleId),
          };
        }

        return old;
      });

      const pruneDeletedCycle = (folders: any[] = []): any[] =>
        folders.map((folder) => ({
          ...folder,
          cycles: Array.isArray(folder?.cycles)
            ? folder.cycles.filter((cycle: any) => Number(cycle?.id) !== deletedCycleId)
            : folder.cycles,
          children: pruneDeletedCycle(folder?.children ?? []),
        }));

      queryClient.setQueriesData({ queryKey: ["releaseFolders"] }, (old: any) => {
        if (!old) return old;
        if (!Array.isArray(old)) return old;
        return pruneDeletedCycle(old);
      });

      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["releaseFolders"] });
    },
  });
};

export const useActivateCycle = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateCycle(cycleId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cycle", cycleId] });
      const prev = queryClient.getQueryData<CycleDetail>(["cycle", cycleId]);
      queryClient.setQueryData<CycleDetail>(["cycle", cycleId], (old) =>
        old ? { ...old, status: "ACTIVE" } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["cycle", cycleId], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["myQueue"] });
      queryClient.invalidateQueries({
        queryKey: ["cycleReportSummary", cycleId],
      });
    },
  });
};

export const useCloseCycle = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload?: CloseCyclePayload) => closeCycle(cycleId, payload),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cycle", cycleId] });
      const prev = queryClient.getQueryData<CycleDetail>(["cycle", cycleId]);
      queryClient.setQueryData<CycleDetail>(["cycle", cycleId], (old) =>
        old ? { ...old, status: "CLOSED" } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["cycle", cycleId], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["myQueue"] });
      queryClient.invalidateQueries({ queryKey: ["cycleCases", cycleId] });
    },
  });
};

export const useReopenCycle = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReopenCyclePayload) => reopenCycle(cycleId, payload),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cycle", cycleId] });
      const prev = queryClient.getQueryData<CycleDetail>(["cycle", cycleId]);
      queryClient.setQueryData<CycleDetail>(["cycle", cycleId], (old) =>
        old ? { ...old, status: "ACTIVE" } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["cycle", cycleId], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["myQueue"] });
      queryClient.invalidateQueries({ queryKey: ["cycleCases", cycleId] });
    },
  });
};

export const useCloneCycle = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CloneCyclePayload) => cloneCycle(cycleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycles"] });
      queryClient.invalidateQueries({ queryKey: ["releaseFolders"] });
    },
  });
};

export const useTriggerRun = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TriggerRunPayload) => triggerRun(cycleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleRuns", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
    },
  });
};

export const useStopRun = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => stopRun(cycleId, runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleRuns", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
    },
  });
};

export const useArchiveInbox = () => {
  const queryClient = useQueryClient();
  const { userPreferenceData } = useAuth();
  const projectId = userPreferenceData?.data?.projectId as number | undefined;
  return useMutation({
    mutationFn: (cycleId: number) => archiveInbox(cycleId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleInbox"] });
    },
  });
};

export const useDismissInviteBanner = () => {
  const queryClient = useQueryClient();
  const { userPreferenceData } = useAuth();
  const projectId = userPreferenceData?.data?.projectId as number | undefined;
  return useMutation({
    mutationFn: () => dismissInviteBanner(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleSoloContext"] });
    },
  });
};

export const useTransitionCycleDefect = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { defectId: number; status: string }) =>
      transitionCycleDefect(vars.defectId, vars.status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleDefects", cycleId] });
      queryClient.invalidateQueries({ queryKey: ["cycle", cycleId] });
      // Fixed → flips the linked step/case to Retest; refresh the execution,
      // cycle cases, coverage and the tester's queue so it reflects everywhere.
      queryClient.invalidateQueries({ queryKey: ["cycleCases"] });
      queryClient.invalidateQueries({ queryKey: ["execution"] });
      queryClient.invalidateQueries({ queryKey: ["myQueue"] });
      queryClient.invalidateQueries({ queryKey: ["cycleCoverage", cycleId] });
      queryClient.invalidateQueries({
        queryKey: ["cycleOrphanedDefects", cycleId],
      });
    },
  });
};

/** BR-65: re-bind an orphaned defect to an Active cycle that has its case. */
export const useRebindCycleDefect = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { defectId: number; targetCycleId: number }) =>
      rebindCycleDefect(vars.defectId, vars.targetCycleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cycleDefects", cycleId] });
      queryClient.invalidateQueries({
        queryKey: ["cycleOrphanedDefects", cycleId],
      });
    },
  });
};

export const useCaptureSnapshot = (cycleId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => captureReportSnapshot(cycleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cycleReportSummary", cycleId],
      });
    },
  });
};
